import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { X } from '@ui/components/icons';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import { PAIR_AMOUNT_DECIMALS } from '@ui/constants/numbersDecimals';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { useGasFee } from '@ui/hooks/useGasFee';
import { useNativeToken } from '@ui/hooks/useNativeToken';
import { toBigValue } from '@ui/lib/bignumber';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Input } from '@ui/lib/park/ui/input';
import { Text } from '@ui/lib/park/ui/text';
import { useEstimateCloseTradeGasLimit } from '@ui/services/contract/hooks/TradingPortalFacet/useEstimateCloseTradeGasLimit';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { CallbackFunction } from '@ui/types/tx';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useMemo, useState } from 'react';
import { Box, Grid, HStack, Stack, styled } from 'styled-system/jsx';

import { TradeLeverageBadge } from '../LeverageBadge';
import { PositionPnl } from '../PositionList/PositionPnl';
import { SizeUsd } from '../SizeUsd';
import { CloseQtyPercentageSelector } from './CloseQtyPercentageSelector';
import { EntryPrice } from './EntryPrice';
import { MarketPrice } from './MarketPrice';
import { labelStyle, valueStyle } from './styles';
import { useCloseTrade } from './useCloseTrade';

export const ClosePositionOverlay = ({
  children,
  position,
  ...rest
}: Dialog.RootProps & {
  position: Position;
}) => {
  const { t } = useTranslation();
  const [closeQtyInput, setCloseQtyInput] = useState('');
  const [closeQtyPercent, setCloseQtyPercent] = useState(100);
  const closeQty = useMemo(() => {
    if (!position.isMergedPosition) return undefined;

    const value = new BigNumber(closeQtyInput);
    if (closeQtyInput === '' || value.isNaN() || value.lte(0)) return undefined;
    if (value.gte(position.size)) return undefined;

    return toBigValue(value, PAIR_AMOUNT_DECIMALS);
  }, [closeQtyInput, position.isMergedPosition, position.size]);
  const closePnlScale = useMemo(() => {
    if (!position.isMergedPosition || position.size.isZero()) return undefined;

    return new BigNumber(closeQtyPercent).div(100);
  }, [closeQtyPercent, position.isMergedPosition, position.size]);
  const setCloseQtyByPercent = (percent: number) => {
    const nextPercent = Math.min(Math.max(Math.round(percent), 0), 100);
    setCloseQtyPercent(nextPercent);
    setCloseQtyInput(
      position.size.multipliedBy(nextPercent).div(100).toFixed(),
    );
  };
  const setCloseQtyByInput = (value: string) => {
    setCloseQtyInput(value);

    const nextValue = new BigNumber(value);
    if (value === '' || nextValue.isNaN() || nextValue.lte(0)) {
      setCloseQtyPercent(100);
      return;
    }

    setCloseQtyPercent(
      Math.min(nextValue.div(position.size).times(100).toNumber(), 100),
    );
  };

  return (
    <Dialog.Root lazyMount unmountOnExit {...rest}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content w="430px" maxW="calc(100vw - 32px)" data-cy="ClosePositionModalContent">
            <Stack gap="3" p="4" pb={0}>
              <HStack p={0} justifyContent="space-between">
                <Dialog.Title p={0}>{t('common.Close Position')}</Dialog.Title>
                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px">
                    <X />
                  </Icon>
                </Dialog.CloseTrigger>
              </HStack>
              <Dialog.Description>
                <HStack
                  p="3"
                  borderRadius="4px"
                  border="1px solid"
                  borderColor="v2.border.default"
                  gap={1}
                  w="full"
                  bg="v2.bg.surface1"
                >
                  <styled.img
                    src={position.pair.image}
                    boxSize="4"
                    borderRadius="2px"
                  />
                  <Text textStyle="headings.default">
                    {position.pair.baseName}
                  </Text>
                  <LongShortBadge isLong={position.isLong} />
                  <TradeLeverageBadge genericTradeParams={position} />
                </HStack>
              </Dialog.Description>
            </Stack>
            <Stack gap={1} p="4" pt={2} borderTopWidth={0}>
              <Box>
                <Grid
                  p={3}
                  rowGap={2}
                  borderRadius={4}
                  border="1px solid"
                  borderColor="v2.border.default"
                  bg="v2.bg.surface1"
                  gridTemplateColumns="1fr auto"
                >
                  <Text css={labelStyle}>{t('common.Close Size')}</Text>
                  <SizeUsd genericTradeParams={position} css={valueStyle} />
                  {position.isMergedPosition && (
                    <>
                      <Text css={labelStyle}>Close Qty</Text>
                      <Input
                        value={closeQtyInput}
                        onChange={(e) =>
                          setCloseQtyByInput(e.currentTarget.value)
                        }
                        placeholder={position.size.toString()}
                        textAlign="right"
                        css={valueStyle}
                      />
                    </>
                  )}

                  <EntryPrice position={position} />

                  <Text css={labelStyle}>{t('common.Mark Price')}</Text>
                  <MarketPrice position={position} valueStyle={valueStyle} />

                  <Text css={labelStyle}>{t('common.PnL')}</Text>
                  <PositionPnl
                    css={valueStyle}
                    position={position}
                    pnlScale={closePnlScale}
                  />
                </Grid>
                {position.isMergedPosition && (
                  <CloseQtyPercentageSelector
                    value={closeQtyPercent}
                    onChange={setCloseQtyByPercent}
                  />
                )}
              </Box>
            </Stack>
            <Stack flexDir="column" gap={3} p="4" pt={0} borderTop="none">
              <HStack
                p="2"
                justifyContent="space-between"
                borderTop="1px solid"
                borderTopColor="v2.border.hairline"
              >
                <Text css={labelStyle}>{t('common.Estimated Gas Fee')}</Text>
                <EstimatedGasFee position={position} closeQty={closeQty} />
              </HStack>
              <Dialog.Context>
                {(api) => {
                  return (
                    <ClosePositionButton
                      position={position}
                      closeQty={closeQty}
                      onSuccess={() => api.setOpen(false)}
                    />
                  );
                }}
              </Dialog.Context>
            </Stack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const EstimatedGasFee = ({
  position,
  closeQty,
}: {
  position: Position;
  closeQty?: bigint;
}) => {
  const { data: gasLimit } = useEstimateCloseTradeGasLimit({
    positionHash: position.id,
    isMergedPosition: position.isMergedPosition,
    closeQty,
  });
  const { data: gasFee } = useGasFee(gasLimit);
  const { symbol } = useNativeToken();

  return (
    <HStack gap={1}>
      <Amount
        css={valueStyle}
        value={gasFee?.gasInNativeToken}
        opts={{
          suffix: ' ' + symbol,
        }}
        data-cy="EstimatedGasFeeInNativeToken"
        data-cy-value={gasFee?.gasInNativeToken}
      />
      <AmountUsd
        value={gasFee?.gasInUsd}
        css={valueStyle}
        opts={{
          prefix: '($',
          suffix: ')',
        }}
        data-cy="EstimatedGasFeeInUsd"
        data-cy-value={gasFee?.gasInUsd}
      />
    </HStack>
  );
};

const ClosePositionButton = ({
  position,
  closeQty,
  onSuccess,
}: {
  position: Position;
  closeQty?: bigint;
  onSuccess?: CallbackFunction;
}) => {
  const { writeContract, isPending } = useCloseTrade({
    position,
    closeQty,
    onSuccess,
  });
  const { t } = useTranslation();

  return (
    <RequireConnectionClickable>
      <Button
        width="full"
        variant="primary"
        size="big"
        loading={isPending}
        onClick={() => writeContract(position.id)}
      >
        {t('common.Close Position')}
      </Button>
    </RequireConnectionClickable>
  );
};
